import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../common/Header";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import ConstanteTable from "../constante/ConstanteTable";
import { useConstanteList } from "../../hooks/useConstanteList";
import { useConsultation } from "../../hooks/useConsultation";
import type { Constante } from "../../types";

const ConstanteForm: React.FC = () => {
  const {
    constantes,
    setConstantes,
    loading,
    error,
    userData,
    refetch,
    pagination,
  } = useConstanteList("doctor");
  const { markAsConsulted } = useConsultation({
    onSuccess: (message) => {
      setSuccessMessage(message);
      refetch();
    },
    onError: (err) => setErrorMessage(err),
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showConsulted, setShowConsulted] = useState(false);
  const [showTodayOnly, setShowTodayOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [constantesPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>("desc");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [processingConsultation, setProcessingConsultation] = useState<
    number | null
  >(null);

  const location = useLocation();

  const totalConstantes = pagination?.totalItems ?? constantes.length;
  const consultedConstantes =
    pagination?.consultedCount ??
    constantes.filter((c: Constante) => c.hasConsultation).length;
  const pendingConstantes =
    pagination?.pendingCount ??
    constantes.filter((c: Constante) => !c.hasConsultation).length;

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [location.state]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [showConsulted, showTodayOnly, debouncedSearch, sortOrder]);

  useEffect(() => {
    refetch({
      todayOnly: showTodayOnly,
      showConsulted,
      search: debouncedSearch,
      page: currentPage,
      size: constantesPerPage,
      sortOrder: sortOrder ?? undefined,
    });
  }, [
    currentPage,
    debouncedSearch,
    constantesPerPage,
    refetch,
    showConsulted,
    showTodayOnly,
    sortOrder,
  ]);

  useEffect(() => {
    const refresh = () =>
      refetch({
        todayOnly: showTodayOnly,
        showConsulted,
        search: debouncedSearch,
        page: currentPage,
        size: constantesPerPage,
        sortOrder: sortOrder ?? undefined,
      });

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    const onFocus = () => refresh();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [
    currentPage,
    debouncedSearch,
    constantesPerPage,
    refetch,
    showConsulted,
    showTodayOnly,
    sortOrder,
  ]);

  const handleMarkAsConsulted = async (constante: Constante): Promise<void> => {
    if (!userData?.id) {
      setErrorMessage("Informations du docteur non disponibles");
      return;
    }

    const patientId = constante.patient?.id || constante.patientId;
    if (!patientId) {
      setErrorMessage("ID du patient non trouvé");
      return;
    }

    setProcessingConsultation(constante.id);

    try {
      await markAsConsulted(constante, Number(userData.id));
      setConstantes((prev: Constante[]) => prev.filter((c: Constante) => c.id !== constante.id));
      setProcessingConsultation(null);
    } catch (err) {
      console.error("Erreur lors de la consultation:", err);
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Erreur lors de la création de la consultation"
      );
      setProcessingConsultation(null);
    }
  };

  const closeSuccessMessage = () => setSuccessMessage(null);
  const closeErrorMessage = () => setErrorMessage(null);

  if (loading) return <LoadingScreen role="doctor" />;
  if (error) return <ResponseMessage message={error} role="doctor" />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        title="Mes Constantes"
        description="Filtrez vos constantes du jour et lancez vos consultations en un clic."
        role="doctor"
      />

      {successMessage && (
        <div className="px-4 sm:px-6 lg:px-8 mb-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg relative">
            <p className="text-sm text-green-700 pr-8">{successMessage}</p>
            <button
              onClick={closeSuccessMessage}
              className="absolute top-2 right-2 text-green-500 hover:text-green-700 text-xl font-bold"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="px-4 sm:px-6 lg:px-8 mb-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg relative">
            <p className="text-sm text-red-700 pr-8">{errorMessage}</p>
            <button
              onClick={closeErrorMessage}
              className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-xl font-bold"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {processingConsultation && (
        <div className="px-4 sm:px-6 lg:px-8 mb-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              Création de la consultation en cours...
            </p>
          </div>
        </div>
      )}

      <div className="px-4 sm:px-6 lg:px-8">
        <ConstanteTable
          role="doctor"
          constantes={constantes}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          showConsulted={showConsulted}
          setShowConsulted={setShowConsulted}
          showTodayOnly={showTodayOnly}
          setShowTodayOnly={setShowTodayOnly}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          constantesPerPage={constantesPerPage}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          pagination={pagination}
          onMarkAsConsulted={handleMarkAsConsulted}
          processingConsultation={processingConsultation}
        />
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Statistiques du jour
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-blue-600">
                {totalConstantes}
              </div>
              <div className="text-sm text-gray-500">
                {showTodayOnly ? "Constantes du jour" : "Constantes filtrées"}
              </div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-green-600">
                {consultedConstantes}
              </div>
              <div className="text-sm text-gray-500">Consultées</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-orange-600">
                {pendingConstantes}
              </div>
              <div className="text-sm text-gray-500">En attente</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-gray-600">
                {totalConstantes}
              </div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstanteForm;

